import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { handleSupabaseError, isForbiddenError, isMissingOrgError } from '@/lib/supabase-error-handler';
import { isCleaningUiRole } from '@/lib/moduleAccess';

type Role = 'owner' | 'coordinator' | 'cleaner';
const ACTIVE_ROLE_STORAGE_KEY = "domio-active-role";
const ACTIVE_ORG_STORAGE_KEY = "domio-active-org-id";

interface Membership {
  id: string;
  org_id: string;
  role: string;
  user_id: string;
  created_at: string | null;
  permissions?: any;
  can_view_financials?: boolean | null;
  can_view_billing?: boolean | null;
}

interface RoleContextType {
  memberships: Membership[];
  currentRole: Role | null;
  setCurrentRole: (role: Role) => void;
  userName: string | null;
  isLoading: boolean;
  availableRoles: Role[];
  currentOrgId: string | null;
  isSnowSeason: boolean;
  refetchMemberships: () => Promise<void>;
  refetchOrgSettings: () => Promise<void>;
  error: string | null;
  permissions: any | null;
  hasPermission: (permissionName: string) => boolean;
}

const RoleContext = createContext<RoleContextType | undefined>(undefined);

interface RoleProviderProps {
  children: ReactNode;
  initialRole?: Role | null;
  initialOrgId?: string | null;
}

export const RoleProvider: React.FC<RoleProviderProps> = ({ 
  children, 
  initialRole = null, 
  initialOrgId = null 
}) => {
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [currentRole, setCurrentRoleState] = useState<Role | null>(initialRole);
  const [userName, setUserName] = useState<string | null>(null);
  // CRITICAL: If initialRole is provided, start with loading false to avoid spinner
  // This prevents double fetching when App.tsx has already fetched the role
  const [isLoading, setIsLoading] = useState(!initialRole);
  const [error, setError] = useState<string | null>(null);
  const [currentOrgId, setCurrentOrgId] = useState<string | null>(initialOrgId);
  const [isSnowSeason, setIsSnowSeason] = useState<boolean>(false);

  // CRITICAL: Refs to prevent stale closure in onAuthStateChange
  // These refs always hold the current value, avoiding stale closure issues
  const isInitializedRef = useRef(!!initialRole); // Mark as initialized if we have initialRole
  const isLoadingRef = useRef(!initialRole); // Set loading ref based on initialRole

  const setCurrentRole = useCallback((role: Role) => {
    console.log('[RoleContext] Zmiana roli na:', role);
    
    // Clear previous state to prevent data leakage between roles
    setCurrentRoleState(null);
    setCurrentOrgId(null);
    
    // Small delay to ensure state is cleared before setting new role
    setTimeout(() => {
      setCurrentRoleState(role);
      
      // Aktualizuj currentOrgId na podstawie wybranej roli
      const membership = memberships.find(m => m.role === role);
      if (membership) {
        setCurrentOrgId(membership.org_id);
        localStorage.setItem(ACTIVE_ROLE_STORAGE_KEY, role);
        if (membership.org_id) {
          localStorage.setItem(ACTIVE_ORG_STORAGE_KEY, membership.org_id);
        } else {
          localStorage.removeItem(ACTIVE_ORG_STORAGE_KEY);
        }
      }
    }, 0);
  }, [memberships]);

  // Internal function to fetch data (used by both public and silent versions)
  const fetchDataInternal = useCallback(async (silent: boolean = false) => {
    // CRITICAL: Cichy Focus - nie ustawiaj loading jeśli aplikacja już zainicjalizowana
    if (!silent && !isInitializedRef.current) {
      isLoadingRef.current = true;
      setIsLoading(true);
    }
    setError(null);
    
    try {
      // Pobierz zalogowanego użytkownika
      const { data: { user }, error: authError } = await supabase.auth.getUser();

      if (authError || !user) {
        console.error('[RoleContext] Błąd autentykacji:', authError);
        setError('Brak zalogowanego użytkownika');
        if (!silent) {
          toast.error('Wymagane logowanie. Zaloguj się, aby kontynuować.');
        }
        // CRITICAL: Always set loading to false, even in error cases
        if (!silent && !isInitializedRef.current) {
          isLoadingRef.current = false;
          setIsLoading(false);
        }
        return;
      }

      const userId = user.id;
      console.log('[RoleContext] Rozpoczynam pobieranie danych dla user_id:', userId, silent ? '(silent)' : '');
      
      // Pobierz członkostwa - RLS automatycznie filtruje po auth.uid()
      // Pobierz również kolumnę permissions
      const { data: membershipData, error: membershipError } = await supabase
        .from('memberships')
        .select('*, permissions')
        .eq('user_id', userId)
        .eq('is_active', true);

      console.log('[RoleContext] Odpowiedź memberships:', { data: membershipData, error: membershipError });

      if (membershipError) {
        console.error('[RoleContext] Błąd pobierania memberships:', membershipError);
        
        // Handle 403 errors specifically
        if (isForbiddenError(membershipError)) {
          if (isMissingOrgError(membershipError)) {
            const errorMessage = 'Brak przypisanej organizacji. Skontaktuj się z administratorem.';
            setError(errorMessage);
            if (!silent) {
              toast.error(errorMessage);
            }
          } else {
            const errorMessage = handleSupabaseError(membershipError, 'RoleContext');
            setError(errorMessage);
          }
        } else {
          const errorMessage = handleSupabaseError(membershipError, 'RoleContext');
          setError(errorMessage);
        }
        // CRITICAL: Always set loading to false even in error cases
        if (!silent && !isInitializedRef.current) {
          isLoadingRef.current = false;
          setIsLoading(false);
        }
      } else {
        console.log('[RoleContext] Pobrane wiersze z tabeli memberships:', membershipData);
        setMemberships(membershipData || []);
        
        // ZASADA: RoleContext nie ma prawa samodzielnie decydować o wyborze roli
        // Jeśli użytkownik ma więcej niż jedno członkostwo, tylko pobiera listę memberships
        // Wybór roli jest dokonywany przez App.tsx (ekran wyboru) lub przez initialRole z props
        if (membershipData && membershipData.length > 0) {
          const roles = membershipData.map(m => m.role as Role);
          console.log('[RoleContext] Dostępne role:', roles);
          console.log('[RoleContext] Liczba członkostw:', membershipData.length);
          
          // NIE ustawiamy automatycznie roli - tylko jeśli mamy initialRole z props
          // Jeśli nie ma initialRole, currentRole pozostaje null
          // App.tsx będzie odpowiedzialny za wyświetlenie ekranu wyboru
          if (!initialRole) {
            console.log('[RoleContext] Brak initialRole - currentRole pozostaje null, App.tsx wyświetli ekran wyboru');
          }
        } else {
          console.log('[RoleContext] Brak ról - użytkownik nie należy do żadnej organizacji');
          setError('Brak przypisanych ról. Skontaktuj się z administratorem.');
          if (!silent) {
            toast.warning('Brak przypisanych ról. Skontaktuj się z administratorem.');
          }
          // CRITICAL: Always set loading to false even when no roles found
          if (!silent && !isInitializedRef.current) {
            isLoadingRef.current = false;
            setIsLoading(false);
          }
        }
      }

      // Pobierz profil użytkownika
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('full_name')
        .eq('id', userId)
        .maybeSingle();

      console.log('[RoleContext] Odpowiedź profiles:', { data: profileData, error: profileError });

      if (profileError) {
        console.error('[RoleContext] Błąd pobierania profilu:', profileError);
        // Nie wyświetlamy błędu dla profilu, bo to nie jest krytyczne
        // Ale logujemy dla debugowania
        if (isForbiddenError(profileError)) {
          console.warn('[RoleContext] Brak uprawnień do profilu (może być normalne dla nowych użytkowników)');
        }
      } else {
        setUserName(profileData?.full_name || 'Użytkownik');
        console.log('[RoleContext] Pobrano imię:', profileData?.full_name);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Nieznany błąd';
      console.error('[RoleContext] Nieoczekiwany błąd:', err);
      setError(`Nieoczekiwany błąd: ${errorMessage}`);
      if (!silent) {
        toast.error(`Wystąpił błąd: ${errorMessage}`);
      }
      // CRITICAL: Always set loading to false in catch block
      if (!silent && !isInitializedRef.current) {
        isLoadingRef.current = false;
        setIsLoading(false);
      }
    } finally {
      // CRITICAL: Always set loading to false in finally block
      // This guarantees loading state is cleared even if error occurred
      if (!silent && !isInitializedRef.current) {
        isLoadingRef.current = false;
        setIsLoading(false);
      }
      // CRITICAL: Mark as initialized after first fetch completes
      if (!isInitializedRef.current) {
        isInitializedRef.current = true;
      }
      console.log('[RoleContext] Zakończono pobieranie danych', silent ? '(silent)' : '');
    }
  }, []);

  // Public function - shows loading state
  const fetchMembershipsAndProfile = useCallback(async () => {
    await fetchDataInternal(false);
  }, [fetchDataInternal]);

  // Silent function - doesn't show loading state (for background refreshes)
  const fetchMembershipsAndProfileSilently = useCallback(async () => {
    await fetchDataInternal(true);
  }, [fetchDataInternal]);

  // UPROSZCZENIE: RoleContext tylko pobiera memberships, nie decyduje o wyborze roli
  // Jeśli initialRole jest przekazane w propsach, użyj go
  // Jeśli nie, currentRole musi pozostać null, dopóki nie zostanie ustawione przez App.tsx
  useEffect(() => {
    // TYMCZASOWO WYŁĄCZONE: sessionStorage dla testów
    // const savedRole = sessionStorage.getItem('domio-selected-role') as Role | null;
    // const savedOrgId = sessionStorage.getItem('domio-selected-org-id');
    
    // If we have initialRole from App.tsx, we still need to fetch memberships
    // to populate the full list, but we can do it silently (without showing loading)
    if (initialRole) {
      // We have initial values from App.tsx - fetch silently to populate full membership list
      console.log('[RoleContext] Mamy initialRole z App.tsx - pobieranie pełnej listy członkostw w tle');
      fetchMembershipsAndProfileSilently();
    } else {
      // No initialRole - fetch data but don't set any role
      // App.tsx will handle role selection via RoleSelectionPage
      console.log('[RoleContext] Brak initialRole - pobieranie memberships bez ustawiania roli');
      fetchMembershipsAndProfile();
      // currentRole pozostaje null - App.tsx wyświetli ekran wyboru
    }
  }, [fetchMembershipsAndProfile, fetchMembershipsAndProfileSilently, initialRole]);

  // Listen for auth state changes and clean up state when user logs out
  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('[RoleContext] Auth state changed:', event, session?.user?.id);
      
      // CRITICAL: Cichy Focus - jeśli aplikacja już zainicjalizowana, nie blokuj UI
      // Zdarzenia SIGNED_IN i TOKEN_REFRESHED wyzwalane focusem nie mogą zmieniać isLoading
      if (isInitializedRef.current && (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED')) {
        console.log('[RoleContext]', event, '- aplikacja już zainicjalizowana, ciche odświeżenie bez blokowania UI');
        // Silently refresh data without showing loading
        await fetchMembershipsAndProfileSilently();
        return;
      }
      
      if (event === 'SIGNED_OUT') {
        // Clear all state when user logs out
        console.log('[RoleContext] Wylogowanie - czyszczenie stanu');
        setMemberships([]);
        setCurrentRoleState(null);
        setUserName(null);
        setCurrentOrgId(null);
        setIsSnowSeason(false);
        setError('Brak zalogowanego użytkownika');
        isLoadingRef.current = false;
        setIsLoading(false);
        isInitializedRef.current = false; // Reset initialization state on logout
        localStorage.removeItem(ACTIVE_ROLE_STORAGE_KEY);
        localStorage.removeItem(ACTIVE_ORG_STORAGE_KEY);
      } else if (event === 'SIGNED_IN') {
        // Refetch data when user signs in (show loading only if not initialized)
        console.log('[RoleContext] Logowanie - odświeżanie danych');
        if (!isInitializedRef.current) {
          await fetchMembershipsAndProfile();
        } else {
          // Already initialized - silent refresh
          await fetchMembershipsAndProfileSilently();
        }
      } else if (event === 'TOKEN_REFRESHED' && session) {
        // CRITICAL: Token refresh happens in background (e.g., when returning to tab)
        // NEVER show loading state during token refresh - silently refresh data
        console.log('[RoleContext] TOKEN_REFRESHED - silently refreshing data in background');
        await fetchMembershipsAndProfileSilently();
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [fetchMembershipsAndProfile, fetchMembershipsAndProfileSilently]);

  // Loguj zmiany stanu
  useEffect(() => {
    console.log('[RoleContext] Stan zmieniony:', {
      memberships: memberships.length,
      currentRole,
      userName,
      isLoading,
      availableRoles: memberships.map(m => m.role),
    });
  }, [memberships, currentRole, userName, isLoading]);

  const availableRoles = memberships
    .map(m => m.role as Role)
    .filter((role): role is Role => isCleaningUiRole(role));

  const fetchOrgSettings = useCallback(async () => {
    if (!currentOrgId) {
      setIsSnowSeason(false);
      return;
    }
    try {
      const { data, error } = await supabase
        .from('organizations')
        .select('is_snow_season')
        .eq('id', currentOrgId)
        .maybeSingle();
      if (error) {
        console.warn('[RoleContext] Błąd pobierania is_snow_season:', error);
        setIsSnowSeason(false);
        return;
      }
      setIsSnowSeason((data as { is_snow_season?: boolean } | null)?.is_snow_season ?? false);
    } catch (err) {
      console.warn('[RoleContext] Błąd pobierania ustawień organizacji:', err);
      setIsSnowSeason(false);
    }
  }, [currentOrgId]);

  useEffect(() => {
    fetchOrgSettings();
  }, [fetchOrgSettings]);

  // Get current membership based on currentRole
  const currentMembership = memberships.find(m => m.role === currentRole);
  const currentPermissions = currentMembership?.permissions || null;

  // Function to check if user has a specific permission
  // Owner role always has all permissions
  const hasPermission = useCallback((permissionName: string): boolean => {
    if (currentRole === 'owner') {
      return true;
    }
    
    if (!currentPermissions || typeof currentPermissions !== 'object') {
      return false;
    }

    return currentPermissions[permissionName] === true;
  }, [currentRole, currentPermissions]);

  return (
    <RoleContext.Provider
      value={{
        memberships,
        currentRole,
        setCurrentRole,
        userName,
        isLoading,
        availableRoles,
        currentOrgId,
        isSnowSeason,
        refetchMemberships: fetchMembershipsAndProfile,
        refetchOrgSettings: fetchOrgSettings,
        error,
        permissions: currentPermissions,
        hasPermission,
      }}
    >
      {children}
    </RoleContext.Provider>
  );
};

export const useRole = (): RoleContextType => {
  const context = useContext(RoleContext);
  if (context === undefined) {
    throw new Error('useRole must be used within a RoleProvider');
  }
  return context;
};
